import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, type Company } from "@/lib/api";

export default function CompaniesPage() {
  const qc = useQueryClient();
  const list = useQuery<Company[]>({
    queryKey: ["md", "companies"],
    queryFn: api.listCompanies,
  });

  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [currency, setCurrency] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.createCompany({
        name,
        natural_key: key || null,
        default_currency: currency || null,
      }),
    onSuccess: () => {
      setName("");
      setKey("");
      setCurrency("");
      setError(null);
      qc.invalidateQueries({ queryKey: ["md", "companies"] });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.detail ?? err.message : String(err));
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!name) return;
    create.mutate();
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-brand-900">Companies</h1>
        <p className="text-sm text-slate-600 mt-1">
          Company-level dimension. Branches, brands and employees hang off
          companies.
        </p>
      </header>

      <section className="bg-white rounded-lg border shadow-sm p-5">
        <h2 className="font-medium">Add a company</h2>
        <form onSubmit={submit} className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name"
            required
            className="rounded-md border px-3 py-2 text-sm"
          />
          <input
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Natural key (optional)"
            className="rounded-md border px-3 py-2 text-sm"
          />
          <input
            value={currency}
            onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))}
            placeholder="Currency (EUR)"
            className="rounded-md border px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={create.isPending || !name}
            className="bg-brand-600 hover:bg-brand-700 text-white text-sm rounded-md px-3 py-2 disabled:opacity-60"
          >
            {create.isPending ? "Saving…" : "Create"}
          </button>
        </form>
        {error && (
          <p className="mt-2 text-sm text-rose-700">{error}</p>
        )}
      </section>

      <section className="bg-white rounded-lg border shadow-sm p-5">
        <h2 className="font-medium">Existing companies</h2>
        {list.isLoading && <p className="text-sm text-slate-500 mt-2">Loading…</p>}
        {list.isError && (
          <p className="text-sm text-rose-700 mt-2">Could not load companies.</p>
        )}
        {list.data && (
          <table className="mt-3 w-full text-sm">
            <thead className="text-slate-500 border-b">
              <tr>
                <th className="text-left py-2">Name</th>
                <th className="text-left py-2">Natural key</th>
                <th className="text-left py-2">Currency</th>
                <th className="text-left py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((c) => (
                <tr key={c.id} className="border-b last:border-none">
                  <td className="py-2">{c.name}</td>
                  <td className="py-2 font-mono text-xs">{c.natural_key ?? "—"}</td>
                  <td className="py-2">{c.default_currency ?? "—"}</td>
                  <td className="py-2">{c.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
